import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, type BudgetsResponse, type BudgetConfig, type ModelPricingResponse, type ModelPricingEntry } from '../api/client';

interface BudgetCard {
  label: string;
  field: keyof BudgetConfig;
  limit: number | undefined;
  spent: number;
  /** When true, `spent` is an aggregate across all tasks — not comparable to a per-task limit */
  aggregate?: boolean;
}

interface CallCountCard {
  label: string;
  field: keyof BudgetConfig;
  limit: number | undefined;
  count: number;
}

function getProgressColor(pct: number): string {
  if (pct > 80) return 'bg-[var(--color-error)]';
  if (pct > 60) return 'bg-[var(--color-warning)]';
  return 'bg-gradient-to-r from-accent-teal to-accent-violet';
}

export function Budgets() {
  const { currentWorkspaceId: workspaceId } = useAuth();

  const [data, setData] = useState<BudgetsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<BudgetConfig>({});
  const [pricing, setPricing] = useState<ModelPricingResponse | null>(null);
  const [pricingSearch, setPricingSearch] = useState('');

  const fetchData = () => {
    if (!workspaceId) return;
    setLoading(true);
    setError(null);
    api.getBudgets(workspaceId)
      .then((d) => {
        setData(d);
        setDraft(d.config);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load budgets.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    api.getModelPricing(workspaceId).then(setPricing).catch(() => {});
  }, [workspaceId]);

  const handleSave = async () => {
    if (!workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateBudgetConfig(workspaceId, draft);
      fetchData();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget config');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!workspaceId) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteBudgetConfig(workspaceId);
      fetchData();
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset budget config');
    } finally {
      setSaving(false);
    }
  };

  if (!workspaceId) {
    return (
      <div className="rounded-xl border border-border-dark bg-surface-1 p-8 text-center text-sm text-text-secondary">
        No workspace found. Please complete onboarding first.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 spinner-gradient" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-[rgba(248,113,113,0.2)] bg-[rgba(248,113,113,0.12)] px-4 py-3 text-sm text-[var(--color-error)]">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const cards: BudgetCard[] = [
    { label: 'Task Budget (per task)', field: 'task_budget_usd', limit: data.config.task_budget_usd, spent: data.spending.task_total, aggregate: true },
    { label: 'User Daily', field: 'user_daily_budget_usd', limit: data.config.user_daily_budget_usd, spent: data.spending.user_daily_total },
    { label: 'User Monthly', field: 'user_monthly_budget_usd', limit: data.config.user_monthly_budget_usd, spent: data.spending.user_monthly_total },
    { label: 'Workspace Daily', field: 'workspace_daily_budget_usd', limit: data.config.workspace_daily_budget_usd, spent: data.spending.workspace_daily_total },
    { label: 'Workspace Monthly', field: 'workspace_monthly_budget_usd', limit: data.config.workspace_monthly_budget_usd, spent: data.spending.workspace_monthly_total },
  ];

  const callCountCards: CallCountCard[] = [
    { label: 'User Daily Calls', field: 'user_daily_budget_calls', limit: data.config.user_daily_budget_calls, count: data.spending.user_daily_call_total ?? 0 },
    { label: 'User Monthly Calls', field: 'user_monthly_budget_calls', limit: data.config.user_monthly_budget_calls, count: data.spending.user_monthly_call_total ?? 0 },
    { label: 'Workspace Daily Calls', field: 'workspace_daily_budget_calls', limit: data.config.workspace_daily_budget_calls, count: data.spending.workspace_daily_call_total ?? 0 },
    { label: 'Workspace Monthly Calls', field: 'workspace_monthly_budget_calls', limit: data.config.workspace_monthly_budget_calls, count: data.spending.workspace_monthly_call_total ?? 0 },
  ];

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-text-primary">Budgets</h1>
            {data.is_custom ? (
              <span className="rounded-full bg-accent-teal/15 px-2.5 py-0.5 text-xs font-medium text-accent-teal">Custom</span>
            ) : (
              <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-text-tertiary">Default</span>
            )}
          </div>
          <p className="mt-1 text-sm text-text-secondary">
            View and configure spending limits for this workspace
          </p>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                onClick={() => { setEditing(false); setDraft(data.config); }}
                className="rounded-lg border border-border-dark px-4 py-2 text-sm text-text-secondary hover:bg-surface-2 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              {data.is_custom && (
                <button
                  onClick={handleReset}
                  className="rounded-lg border border-[rgba(248,113,113,0.3)] px-4 py-2 text-sm text-[var(--color-error)] hover:bg-[rgba(248,113,113,0.08)] transition-colors"
                  disabled={saving}
                >
                  Reset to Default
                </button>
              )}
              <button
                onClick={handleSave}
                className="rounded-lg bg-accent-teal px-4 py-2 text-sm font-medium text-white hover:bg-accent-teal/90 transition-colors"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="rounded-lg bg-accent-teal px-4 py-2 text-sm font-medium text-white hover:bg-accent-teal/90 transition-colors"
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((card) => {
          const limit = editing ? draft[card.field] : card.limit;
          const hasLimit = limit !== undefined && limit > 0;
          // For per-task aggregate cards, don't compute % against the per-task limit
          const isAggregate = card.aggregate && hasLimit;
          const pct = hasLimit && !isAggregate ? Math.min(100, (card.spent / limit!) * 100) : 0;
          const remaining = hasLimit && !isAggregate ? Math.max(0, limit! - card.spent) : undefined;
          const isNearLimit = !isAggregate && pct > 80;

          return (
            <div
              key={card.label}
              className={`rounded-xl border border-border-dark bg-surface-1 p-6 transition-shadow ${isNearLimit ? 'shadow-[0_0_15px_rgba(248,113,113,0.15)]' : ''}`}
            >
              <h3 className="text-sm font-medium text-text-secondary">{card.label}</h3>
              {editing ? (
                <div className="mt-2">
                  <label className="text-xs text-text-tertiary">Limit (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none"
                    value={draft[card.field] ?? ''}
                    onChange={(e) => {
                      const val = e.target.value === '' ? undefined : parseFloat(e.target.value);
                      setDraft({ ...draft, [card.field]: val });
                    }}
                    placeholder="No limit"
                  />
                </div>
              ) : hasLimit && isAggregate ? (
                /* Per-task budget: show total spent + per-task limit separately */
                <div className="mt-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-text-primary">
                      ${card.spent.toFixed(4)}
                    </span>
                    <span className="text-sm text-text-tertiary">total spent</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 rounded-md bg-surface-2 px-3 py-2">
                    <span className="text-xs text-text-secondary">
                      Each task limited to <span className="font-semibold text-text-primary">${limit!.toFixed(2)}</span>
                    </span>
                  </div>
                  <p className="mt-1.5 text-[11px] text-text-tertiary">
                    Enforced per task. Total is the sum of all tasks.
                  </p>
                </div>
              ) : hasLimit ? (
                <>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-2xl font-bold text-text-primary">
                      ${card.spent.toFixed(4)}
                    </span>
                    <span className="text-sm text-text-secondary">
                      / ${limit!.toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-3">
                    <div
                      className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
                      role="progressbar"
                      aria-valuenow={Math.round(pct)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${card.label} usage`}
                    >
                      <div
                        className={`h-full rounded-full transition-all ${getProgressColor(pct)}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-xs text-text-secondary">
                      <span>{pct.toFixed(1)}% used</span>
                      <span>${remaining!.toFixed(4)} remaining</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-2">
                  <span className="text-2xl font-bold text-text-primary">
                    ${card.spent.toFixed(4)}
                  </span>
                  <p className="mt-1 text-xs text-text-secondary">No limit configured</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Call-Count Budgets */}
      <div className="mt-8">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Call-Count Limits</h2>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {callCountCards.map((card) => {
            const limit = editing ? (draft[card.field] as number | undefined) : card.limit;
            const hasLimit = limit !== undefined && limit > 0;
            const pct = hasLimit ? Math.min(100, (card.count / limit!) * 100) : 0;
            const remaining = hasLimit ? Math.max(0, limit! - card.count) : undefined;
            const isNearLimit = pct > 80;

            return (
              <div
                key={card.label}
                className={`rounded-xl border border-border-dark bg-surface-1 p-6 transition-shadow ${isNearLimit ? 'shadow-[0_0_15px_rgba(248,113,113,0.15)]' : ''}`}
              >
                <h3 className="text-sm font-medium text-text-secondary">{card.label}</h3>
                {editing ? (
                  <div className="mt-2">
                    <label className="text-xs text-text-tertiary">Max calls</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none"
                      value={draft[card.field] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? undefined : parseInt(e.target.value);
                        setDraft({ ...draft, [card.field]: val });
                      }}
                      placeholder="No limit"
                    />
                  </div>
                ) : hasLimit ? (
                  <>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-text-primary">
                        {card.count}
                      </span>
                      <span className="text-sm text-text-secondary">
                        / {limit!}
                      </span>
                      <span className="text-xs text-text-tertiary ml-1">calls</span>
                    </div>
                    <div className="mt-3">
                      <div
                        className="h-2 w-full overflow-hidden rounded-full bg-surface-2"
                        role="progressbar"
                        aria-valuenow={Math.round(pct)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${card.label} usage`}
                      >
                        <div
                          className={`h-full rounded-full transition-all ${getProgressColor(pct)}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="mt-1 flex justify-between text-xs text-text-secondary">
                        <span>{pct.toFixed(1)}% used</span>
                        <span>{remaining!} remaining</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="mt-2">
                    <span className="text-2xl font-bold text-text-primary">
                      {card.count}
                    </span>
                    <span className="text-xs text-text-tertiary ml-2">calls</span>
                    <p className="mt-1 text-xs text-text-secondary">No limit configured</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Operational limits */}
      <div className="mt-8">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Operational Limits</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border-dark bg-surface-1 p-5">
            <p className="text-sm text-text-secondary">Max Steps per Task</p>
            {editing ? (
              <input
                type="number"
                min="1"
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none"
                value={draft.max_steps_per_task ?? ''}
                onChange={(e) => setDraft({ ...draft, max_steps_per_task: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                placeholder="Unlimited"
              />
            ) : (
              <p className="mt-1 text-xl font-bold text-text-primary">
                {data.config.max_steps_per_task ?? 'Unlimited'}
              </p>
            )}
          </div>
          <div className="rounded-xl border border-border-dark bg-surface-1 p-5">
            <p className="text-sm text-text-secondary">Max Retries per Call</p>
            {editing ? (
              <input
                type="number"
                min="1"
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none"
                value={draft.max_retries_per_call ?? ''}
                onChange={(e) => setDraft({ ...draft, max_retries_per_call: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                placeholder="Unlimited"
              />
            ) : (
              <p className="mt-1 text-xl font-bold text-text-primary">
                {data.config.max_retries_per_call ?? 'Unlimited'}
              </p>
            )}
          </div>
          <div className="rounded-xl border border-border-dark bg-surface-1 p-5">
            <p className="text-sm text-text-secondary">Max Wall Clock</p>
            {editing ? (
              <input
                type="number"
                min="1000"
                step="1000"
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none"
                value={draft.max_wall_clock_ms ?? ''}
                onChange={(e) => setDraft({ ...draft, max_wall_clock_ms: e.target.value === '' ? undefined : parseInt(e.target.value) })}
                placeholder="Unlimited (ms)"
              />
            ) : (
              <p className="mt-1 text-xl font-bold text-text-primary">
                {data.config.max_wall_clock_ms
                  ? `${(data.config.max_wall_clock_ms / 1000).toFixed(0)}s`
                  : 'Unlimited'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Token Pricing */}
      {pricing && (
        <div className="mt-8">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Token Pricing</h2>
              <p className="mt-0.5 text-xs text-text-secondary">
                Prices used to compute LLM request costs.
                {pricing.has_workspace_overrides && (
                  <span className="ml-1 text-accent-teal">Workspace overrides active.</span>
                )}
              </p>
            </div>
            <input
              type="text"
              placeholder="Search models..."
              className="w-56 rounded-lg border border-border-dark bg-surface-2 px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none"
              value={pricingSearch}
              onChange={(e) => setPricingSearch(e.target.value)}
            />
          </div>
          <div className="overflow-hidden rounded-xl border border-border-dark">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-dark bg-surface-1">
                  <th className="px-4 py-2.5 text-left font-medium text-text-secondary">Model</th>
                  <th className="px-4 py-2.5 text-right font-medium text-text-secondary">Input (per 1M)</th>
                  <th className="px-4 py-2.5 text-right font-medium text-text-secondary">Output (per 1M)</th>
                  <th className="px-4 py-2.5 text-right font-medium text-text-secondary">Source</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(pricing.models)
                  .filter(([model]) => !pricingSearch || model.toLowerCase().includes(pricingSearch.toLowerCase()))
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([model, entry]) => (
                    <tr key={model} className="border-b border-border-dark last:border-0 hover:bg-surface-1/50">
                      <td className="px-4 py-2 font-mono text-xs text-text-primary">{model}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-text-primary">
                        ${(entry.input_per_token * 1e6).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-text-primary">
                        ${(entry.output_per_token * 1e6).toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          entry.source === 'workspace'
                            ? 'bg-accent-teal/15 text-accent-teal'
                            : 'bg-surface-2 text-text-tertiary'
                        }`}>
                          {entry.source}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
